import React, { useEffect, useMemo, useRef, useState } from "react";
import { User } from "../../../../models/user.model";
import { getUsers } from "../../../../api/getUsers";
import { getUserDepositsData } from "../../../../api/getUserDepositsData";
import { DepositRegister } from "../../../../models/depositregister";


const SYSTEM_IDS = [2, 4] as const;

type DepositOption = DepositRegister & { __systemId: number };

interface AddHoldingDepositModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (data: any) => void;
    isSubmitting?: boolean;
}

export const AddHoldingDepositModal: React.FC<AddHoldingDepositModalProps> = ({ isOpen, onClose, onSubmit, isSubmitting = false }) => {
    const [users, setUsers] = useState<User[]>([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);

    // Deposits
    const [deposits, setDeposits] = useState<DepositOption[]>([]);
    const [depositsLoading, setDepositsLoading] = useState(false);
    const [depositsError, setDepositsError] = useState<string | null>(null);
    const [depositRegisterId, setDepositRegisterId] = useState<string>("");

    // Form
    const [date, setDate] = useState(new Date().toISOString().split("T")[0]);
    const [investedEur, setInvestedEur] = useState<string>("");
    const [netInvestedEur, setNetInvestedEur] = useState<string>("");
    const [investedUsdt, setInvestedUsdt] = useState<string>("");

    const dropdownRef = useRef<HTMLDivElement>(null);

    // Users betöltése
    useEffect(() => {
        if (!isOpen) return;
        const loadUsers = async () => {
            try {
                setUsers(await getUsers());
            } catch (error) {
                console.error("Failed to load users", error);
            }
        };
        loadUsers();
    }, [isOpen]);

    // Reset záráskor
    useEffect(() => {
        if (isOpen) return;
        setSearchTerm("");
        setSelectedUser(null);
        setDeposits([]);
        setDepositsError(null);
        setDepositRegisterId("");
        setInvestedEur("");
        setNetInvestedEur("");
        setInvestedUsdt("");
        setDate(new Date().toISOString().split("T")[0]);
    }, [isOpen]);

    // Kattintás a dropdownon kívül
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsUserDropdownOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    // Befektetések lekérése a kiválasztott userhez (system 2 ÉS 4)
    useEffect(() => {
        const userId = selectedUser?.user_id;
        if (!userId) {
            setDeposits([]);
            setDepositRegisterId("");
            return;
        }

        let cancelled = false;

        const loadDeposits = async () => {
            setDepositsLoading(true);
            setDepositsError(null);
            setDepositRegisterId("");

            const results = await Promise.allSettled(
                SYSTEM_IDS.map(sid => getUserDepositsData(userId, sid))
            );

            if (cancelled) return;

            const merged: DepositOption[] = [];
            let hadError = false;

            results.forEach((res, idx) => {
                if (res.status === "fulfilled") {
                    (res.value ?? []).forEach(d => merged.push({ ...d, __systemId: SYSTEM_IDS[idx] }));
                } else {
                    hadError = true;
                    console.error(`Deposits (system ${SYSTEM_IDS[idx]}) betöltése sikertelen`, res.reason);
                }
            });

            // Duplikátumok kiszűrése id alapján
            const unique = Array.from(new Map(merged.map(d => [d.id, d])).values());

            setDeposits(unique);
            setDepositsError(hadError && unique.length === 0 ? "Nem sikerült betölteni a befektetéseket." : null);
            setDepositsLoading(false);
        };

        loadDeposits();
        return () => { cancelled = true; };
    }, [selectedUser?.user_id]);

    const filteredUsers = useMemo(() => {
        const lower = searchTerm.toLowerCase();
        return users.filter(u =>
            u.name?.toLowerCase().includes(lower) || u.email?.toLowerCase().includes(lower)
        );
    }, [searchTerm, users]);

    const handleUserSelect = (user: User) => {
        setSelectedUser(user);
        setSearchTerm(user.name || user.email || "");
        setIsUserDropdownOpen(false);
    };

    const depositLabel = (d: DepositOption) => {
        const dateLabel = (d as any).date
            ? new Date((d as any).date).toLocaleDateString("hu-HU")
            : "—";
        const amount = (d as any).invested_amount_usdt ?? (d as any).invested_amount_eur;
        const amountLabel = amount != null ? ` · ${Number(amount).toLocaleString("hu-HU")}` : "";
        return `#${d.id} · ${dateLabel}${amountLabel} · (rendszer ${d.__systemId})`;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!selectedUser) return alert("Kérlek válassz ügyfelet!");
        if (!depositRegisterId) return alert("Kérlek válassz kapcsolódó befektetést!");

        onSubmit({
            user_id: selectedUser.user_id,
            depositRegisterId: Number(depositRegisterId),
            date,
            invested_amount_eur: Number(investedEur) || 0,
            net_invested_amount_eur: Number(netInvestedEur) || 0,
            invested_amount_usdt: investedUsdt === "" ? null : Number(investedUsdt),
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-dark-blue/60 backdrop-blur-sm">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-xl overflow-hidden border border-cream">
                <div className="flex items-center justify-between p-6 bg-cream/30 border-b border-cream">
                    <h2 className="text-xl font-bold text-dark-brown">Új befizetés létrehozása</h2>
                    <button onClick={onClose} className="text-light-blue hover:text-error transition-colors">
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="p-6 space-y-4">
                    {/* Ügyfél */}
                    <div className="relative" ref={dropdownRef}>
                        <label className="block text-sm font-semibold text-dark-blue mb-1">Ügyfél kiválasztása</label>
                        <input
                            type="text"
                            placeholder="Kezdd el gépelni a nevet..."
                            className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg focus:outline-none focus:ring-2 focus:ring-brown/50"
                            value={searchTerm}
                            onChange={(e) => {
                                setSearchTerm(e.target.value);
                                setIsUserDropdownOpen(true);
                                setSelectedUser(null);
                            }}
                            onFocus={() => setIsUserDropdownOpen(true)}
                        />
                        {isUserDropdownOpen && filteredUsers.length > 0 && (
                            <div className="absolute z-10 w-full mt-1 bg-white border border-cream rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                {filteredUsers.map((user) => (
                                    <button
                                        key={user.user_id}
                                        type="button"
                                        className="w-full text-left px-4 py-3 hover:bg-ultra-light/50 border-b last:border-0"
                                        onClick={() => handleUserSelect(user)}
                                    >
                                        <span className="font-semibold block">{user.name}</span>
                                        <span className="text-xs text-light-blue">{user.email}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    {/* Kapcsolódó befektetés */}
                    <div>
                        <label className="block text-sm font-semibold text-dark-blue mb-1">
                            Kapcsolódó befektetés
                            {depositsLoading && <span className="ml-2 text-[11px] font-normal text-light-blue italic">betöltés...</span>}
                        </label>
                        <select
                            value={depositRegisterId}
                            onChange={(e) => setDepositRegisterId(e.target.value)}
                            disabled={!selectedUser || depositsLoading || deposits.length === 0}
                            required
                            className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <option value="">
                                {!selectedUser
                                    ? "Előbb válassz ügyfelet"
                                    : deposits.length === 0 && !depositsLoading
                                        ? "Nincs elérhető befektetés"
                                        : "Válassz..."}
                            </option>
                            {deposits.map(d => (
                                <option key={`${d.__systemId}-${d.id}`} value={d.id}>{depositLabel(d)}</option>
                            ))}
                        </select>
                        {depositsError && (
                            <p className="mt-1 text-[11px] text-error font-semibold">{depositsError}</p>
                        )}
                    </div>

                    <hr className="border-cream my-2" />

                    {/* Dátum */}
                    <div>
                        <label className="block text-sm font-semibold text-dark-blue mb-1">Dátum</label>
                        <input
                            type="date"
                            required
                            value={date}
                            onChange={(e) => setDate(e.target.value)}
                            className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg"
                        />
                    </div>

                    {/* Összegek */}
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label className="block text-sm font-semibold text-dark-blue mb-1">Befizetett (EUR)</label>
                            <input
                                type="number"
                                step="any"
                                required
                                value={investedEur}
                                onChange={(e) => setInvestedEur(e.target.value)}
                                className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg font-mono"
                                placeholder="0.00"
                            />
                        </div>
                        <div>
                            <label className="block text-sm font-semibold text-dark-blue mb-1">Nettó befizetett (EUR)</label>
                            <input
                                type="number"
                                step="any"
                                required
                                value={netInvestedEur}
                                onChange={(e) => setNetInvestedEur(e.target.value)}
                                className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg font-mono"
                                placeholder="0.00"
                            />
                            <p className="mt-1 text-[10px] text-light-blue italic">A levonások utáni összeg</p>
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-semibold text-dark-blue mb-1">Befizetett összeg (USD)</label>
                        <input
                            type="number"
                            step="any"
                            value={investedUsdt}
                            onChange={(e) => setInvestedUsdt(e.target.value)}
                            className="w-full px-4 py-2.5 bg-back border border-light-blue/20 rounded-lg font-mono"
                            placeholder="0.00"
                        />
                    </div>

                    <div className="flex gap-3 pt-4 border-t border-cream">
                    <button
                        type="button"
                        onClick={onClose}
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2.5 rounded-lg border border-light-blue/30 text-light-blue font-semibold hover:bg-back transition-colors disabled:opacity-50"
                    >
                        Mégse
                    </button>
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex-1 px-4 py-2.5 rounded-lg bg-brown text-white font-semibold hover:bg-dark-brown transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isSubmitting ? "Mentés..." : "Mentés"}
                    </button>
                </div>
                </form>
            </div>
        </div>
    );
};